import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { holidayAPI } from '../../services';
import { toast } from 'react-toastify';
import { useBranch } from '../../context/BranchContext';

export default function HolidayFormModal({ show, onHide, holiday, onSuccess }) {
  const { branches } = useBranch();
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
    is_fixed: false,
    is_active: true,
    branch_id: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (holiday) {
      setFormData({
        name: holiday.name || '',
        date: holiday.date ? holiday.date.split('T')[0] : '',
        month: holiday.month || new Date().getMonth() + 1,
        day: holiday.day || new Date().getDate(),
        is_fixed: holiday.is_fixed || false,
        is_active: holiday.is_active !== undefined ? holiday.is_active : true,
        branch_id: holiday.branch_id || null,
      });
    } else {
      setFormData({
        name: '',
        date: '',
        month: new Date().getMonth() + 1,
        day: new Date().getDate(),
        is_fixed: false,
        is_active: true,
        branch_id: null,
      });
    }
  }, [holiday, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Vui lòng nhập tên ngày lễ');
      return;
    }

    if (!formData.date && formData.is_fixed) {
      toast.error('Vui lòng chọn ngày');
      return;
    }

    try {
      setLoading(true);
      const data = {
        name: formData.name,
        ...(formData.is_fixed
          ? {
              date: formData.date,
              month: new Date(formData.date).getMonth() + 1,
              day: new Date(formData.date).getDate(),
            }
          : {
              month: formData.month,
              day: formData.day,
            }),
        is_fixed: formData.is_fixed,
        is_active: formData.is_active,
        branch_id: formData.branch_id === 'all' ? null : formData.branch_id || null,
      };

      if (holiday) {
        await holidayAPI.updateHoliday(holiday.id, data);
        toast.success('Đã cập nhật ngày lễ thành công');
      } else {
        await holidayAPI.createHoliday(data);
        toast.success('Đã tạo ngày lễ thành công');
      }

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Cannot save holiday', error);
      toast.error(error?.message || 'Không thể lưu ngày lễ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{holiday ? 'Sửa ngày lễ' : 'Thêm ngày lễ mới'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning mb-3">
            <strong>⚠️ Lưu ý:</strong> Ngày lễ này sẽ làm tăng lương các ca trùng ngày.
            <br />
            Ví dụ: Ca thường → x2.0, Ca đêm + lễ → x2.0 (theo cấu hình hiện tại)
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Tên ngày lễ <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="VD: Tết Dương Lịch"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Phạm vi áp dụng</Form.Label>
            <Form.Select
              value={formData.branch_id || 'all'}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  branch_id: e.target.value === 'all' ? null : e.target.value,
                })
              }
            >
              <option value="all">Tất cả chi nhánh</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Loại ngày lễ</Form.Label>
            <div>
              <Form.Check
                type="radio"
                label="Cố định theo ngày dương lịch (VD: 1/1 hàng năm)"
                checked={formData.is_fixed}
                onChange={() => setFormData({ ...formData, is_fixed: true })}
                className="mb-2"
              />
              <Form.Check
                type="radio"
                label="Cố định theo thứ (nâng cao - VD: Chủ nhật đầu tiên tháng 5)"
                checked={!formData.is_fixed}
                onChange={() => setFormData({ ...formData, is_fixed: false })}
              />
            </div>
          </Form.Group>

          {formData.is_fixed ? (
            <Form.Group className="mb-3">
              <Form.Label>Ngày <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </Form.Group>
          ) : (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tháng</Form.Label>
                  <Form.Select
                    value={formData.month}
                    onChange={(e) => setFormData({ ...formData, month: Number(e.target.value) })}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>
                        Tháng {m}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ngày</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={31}
                    value={formData.day}
                    onChange={(e) => setFormData({ ...formData, day: Number(e.target.value) })}
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              label="Kích hoạt"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Hủy
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Đang lưu...' : holiday ? 'Cập nhật' : 'Tạo mới'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

